/**
 * Home Service - API Layer with DTOs
 *
 * THIS IS WHERE AXIOS CALLS LIVE!
 *
 * Benefits:
 * - Centralized API calls (no axios in components/store)
 * - Automatic DTO transformation
 * - Error handling in one place
 * - Type-safe with TypeScript
 * - Easy to mock for testing
 */

import { AxiosError } from 'axios';
import { apiClient } from './api/client';
import { HomeDTO, RoomDTO } from '@/models/dtos';

const apiPrefix = process.env.VUE_APP_API_PREFIX;

export class HomeService {
  /**
   * ✅ THIS IS WHERE AXIOS CALLS HAPPEN!
   *
   * Get all homes for a user
   * Returns validated DTOs, not raw API response
   */
  static async getHomes(userUid: string): Promise<HomeDTO[]> {
    try {
      // ✅ AXIOS CALL HERE
      const response = await apiClient.axios.get(`/homes`, {
        params: { userUid },
      });

      // Handle different API response shapes
      let homesArray: any[] = [];
      if (Array.isArray(response.data)) {
        homesArray = response.data;
      } else if (response.data && Array.isArray(response.data.data)) {
        homesArray = response.data.data;
      }

      // ✅ Transform to type-safe DTOs - validation happens here
      return HomeDTO.fromApiResponseArray(homesArray);
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error('[HomeService] Error fetching homes:', axiosError.message);
      throw new Error(`Failed to fetch homes: ${axiosError.message}`);
    }
  }

  /**
   * ✅ AXIOS CALL HERE!
   *
   * Get room details for a home
   * Returns validated RoomDTO
   */
  static async getRoom(
    homeUid: string,
    roomId: string
  ): Promise<RoomDTO> {
    try {
      // ✅ AXIOS CALL HERE
      const response = await apiClient.axios.get(
        `/homes/${homeUid}/rooms/${roomId}`,
        {}
      );

      // ✅ Transform to type-safe DTO
      return RoomDTO.fromApiResponse(response.data);
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error(`[HomeService] Error fetching room ${roomId}:`, axiosError.message);

      // ✅ Return minimal valid DTO instead of failing
      return new RoomDTO({
        uid: roomId,
        name: `Room ${roomId}`,
        devices: [],
        gateways: [],
      });
    }
  }

  /**
   * ✅ COMPLEX AXIOS ORCHESTRATION HERE!
   *
   * Get homes with full room details
   * Handles complex nested loading with type-safe DTOs
   */
  static async getHomesWithRooms(
    userUid: string
  ): Promise<HomeDTO[]> {
    try {
      // Get homes (axios call inside)
      const homeDTOs = await this.getHomes(userUid);

      // Load room details for each home (multiple axios calls)
      const homesWithRooms = await Promise.all(
        homeDTOs.map(async (homeDTO) => {
          // Use roomIds (strings) not rooms (DTOs)
          const roomIds = homeDTO.roomIds || [];

          if (roomIds.length === 0) {
            return homeDTO;
          }

          // Fetch each room's details (axios calls inside)
          const roomDTOs = await Promise.all(
            roomIds.map(roomId =>
              this.getRoom(homeDTO.uid, roomId)
            )
          );

          // Return new HomeDTO with populated rooms
          return new HomeDTO({
            uid: homeDTO.uid,
            name: homeDTO.name,
            userUid: homeDTO.userUid,
            deviceUids: homeDTO.deviceUids,
            gatewayUids: homeDTO.gatewayUids,
            rooms: roomDTOs
          });
        })
      );

      return homesWithRooms;
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error('[HomeService] Error fetching homes with rooms:', axiosError.message);
      throw new Error(`Failed to fetch homes with rooms: ${axiosError.message}`);
    }
  }

  /**
   * ✅ AXIOS POST HERE!
   *
   * Create a new home
   * Accepts plain data, returns type-safe DTO
   */
  static async createHome(
    homeData: { name: string; userUid: string }
  ): Promise<HomeDTO> {
    try {
      // ✅ AXIOS POST CALL HERE
      const response = await apiClient.axios.post(
        `/homes`,
        homeData,
        {}
      );

      // ✅ Return validated, type-safe DTO
      return HomeDTO.fromApiResponse(response.data);
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error('[HomeService] Error creating home:', axiosError.message);
      throw new Error(`Failed to create home: ${axiosError.message}`);
    }
  }

  /**
   * ✅ AXIOS PATCH HERE!
   *
   * Update a home
   * Accepts updates, returns type-safe DTO
   */
  static async updateHome(
    homeUid: string,
    updates: Partial<{ name: string }>
  ): Promise<HomeDTO> {
    try {
      // ✅ AXIOS PATCH CALL HERE
      const response = await apiClient.axios.patch(
        `/homes/${homeUid}`,
        updates,
        {}
      );

      return HomeDTO.fromApiResponse(response.data);
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error('[HomeService] Error updating home:', axiosError.message);
      throw new Error(`Failed to update home: ${axiosError.message}`);
    }
  }

  /**
   * ✅ AXIOS DELETE HERE!
   *
   * Delete a home
   */
  static async deleteHome(homeUid: string): Promise<boolean> {
    try {
      // ✅ AXIOS DELETE CALL HERE
      await apiClient.axios.delete(
        `/homes/${homeUid}`,
        {}
      );
      return true;
    } catch (error) {
      const axiosError = error as AxiosError;
      console.error('[HomeService] Error deleting home:', axiosError.message);
      throw new Error(`Failed to delete home: ${axiosError.message}`);
    }
  }
}
